import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, ShieldCheck, GraduationCap } from "lucide-react";
import { Reveal } from "@/components/ScrollReveal";

export default function CursusInvestment({ cursus }) {
    const certTitle = cursus?.certificate_title || "Statut Expert PA Cursus Physio";
    return (
        <section
            className="relative bg-brand-sand py-24 md:py-32"
            data-testid="cursus-investment"
        >
            <div className="mx-auto max-w-7xl px-6">
                <Reveal>
                    <span className="text-[11px] font-semibold uppercase tracking-[0.32em] text-brand-orange">
                        Investissement
                    </span>
                    <h2 className="mt-5 max-w-4xl font-display font-normal leading-[0.95] tracking-tightest text-brand-ink text-[40px] sm:text-5xl lg:text-[72px]">
                        Choisissez <em className="italic text-brand-smoke">votre rythme.</em>
                    </h2>
                </Reveal>

                <div className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-2 lg:gap-8">
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-10%" }}
                        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                        className="rounded-3xl border border-brand-ink/10 bg-white p-10 shadow-premium md:p-12"
                        data-testid="cursus-invest-unit"
                    >
                        <div className="text-[11px] font-semibold uppercase tracking-[0.3em] text-brand-smoke">
                            Formation à l'unité
                        </div>
                        <div className="mt-8 flex items-baseline gap-3">
                            <div className="font-display text-6xl font-normal text-brand-ink md:text-7xl">390&nbsp;€</div>
                        </div>
                        <div className="mt-2 text-sm text-brand-smoke">par formation · 7h</div>
                        <ul className="mt-10 space-y-3 text-sm text-brand-ink/75">
                            {["1 formation sur 7 heures", "Attestation de fin de formation", "Prise en charge FIF PL / DPC"].map((b) => (
                                <li key={b} className="flex items-center gap-3">
                                    <Check className="h-4 w-4 text-brand-orange" /> {b}
                                </li>
                            ))}
                        </ul>
                        <Link
                            to="/sessions"
                            className="mt-10 inline-flex w-full items-center justify-center gap-2 rounded-full border border-brand-ink/25 px-6 py-3.5 text-sm font-semibold text-brand-ink transition-all hover:border-brand-ink hover:bg-brand-ink hover:text-white"
                            data-testid="cursus-invest-unit-cta"
                        >
                            Voir les sessions
                            <ArrowRight className="h-4 w-4" />
                        </Link>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-10%" }}
                        transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
                        className="relative rounded-3xl border-2 border-brand-orange bg-brand-ink p-10 text-white md:p-12"
                        style={{ boxShadow: "0 30px 70px -20px rgba(232,135,60,0.25)" }}
                        data-testid="cursus-invest-full"
                    >
                        <div className="absolute right-6 top-6 inline-flex items-center gap-1.5 rounded-full bg-brand-orange px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-white">
                            <GraduationCap className="h-3 w-3" /> Statut Expert PA
                        </div>
                        <div className="text-[11px] font-semibold uppercase tracking-[0.3em] text-brand-orange">
                            Cursus complet
                        </div>
                        <div className="mt-8 flex items-baseline gap-4">
                            <div className="font-display text-6xl font-normal md:text-7xl">1&nbsp;170&nbsp;€</div>
                        </div>
                        <div className="mt-2 text-sm text-white/55">le cursus · 21h · 3 formations</div>

                        {/* Certificate highlight block */}
                        <div className="mt-6 rounded-2xl border border-brand-orange/40 bg-brand-orange/[0.08] p-5" data-testid="cursus-certificate-highlight">
                            <div className="flex items-start gap-3">
                                <GraduationCap className="mt-0.5 h-5 w-5 flex-shrink-0 text-brand-orange" />
                                <div>
                                    <div className="font-display text-base text-white md:text-lg">
                                        {certTitle}
                                    </div>
                                    <div className="mt-2 text-xs leading-relaxed text-white/65 md:text-sm">
                                        En complétant les 3 formations de ce cursus, vous franchissez un palier vers le Statut d'Expert en Prévention Active, reconnaissance officielle de Cursus Physio.
                                    </div>
                                </div>
                            </div>
                        </div>

                        <ul className="mt-8 space-y-3 text-sm text-white/85">
                            {["3 formations progressives · 21h", "Progression pensée, cohérente", "Expertise clinique approfondie", "Prise en charge FIF PL / DPC"].map((b) => (
                                <li key={b} className="flex items-center gap-3">
                                    <Check className="h-4 w-4 text-brand-orange" /> {b}
                                </li>
                            ))}
                        </ul>
                        <Link
                            to="/sessions"
                            className="mt-10 inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand-orange px-6 py-3.5 text-sm font-semibold text-white transition-all hover:bg-[#d9772c]"
                            data-testid="cursus-invest-full-cta"
                        >
                            Composer mon cursus
                            <ArrowRight className="h-4 w-4" />
                        </Link>
                    </motion.div>
                </div>

                {/* Horizontal prise-en-charge band */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-10%" }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                    className="mt-10 flex flex-col items-center justify-between gap-6 rounded-3xl border border-brand-ink/10 bg-white px-8 py-7 text-center md:flex-row md:text-left"
                    data-testid="cursus-prise-en-charge-band"
                >
                    <div className="flex items-center justify-center gap-6">
                        <div className="flex h-14 w-14 items-center justify-center rounded-xl border border-brand-orange/40 bg-brand-orange/10 font-display text-base italic text-brand-orange">
                            FIF
                        </div>
                        <div className="flex h-14 w-14 items-center justify-center rounded-xl border border-brand-orange/40 bg-brand-orange/10 font-display text-base italic text-brand-orange">
                            DPC
                        </div>
                    </div>
                    <div className="flex-1 md:px-8">
                        <div className="inline-flex items-center gap-2 text-sm font-semibold text-brand-ink">
                            <ShieldCheck className="h-4 w-4 text-brand-orange" />
                            Pris en charge à 100% sous conditions d'éligibilité
                        </div>
                        <div className="mt-2 text-xs text-brand-smoke">
                            Nous vous fournissons tous les documents : convention, programme, facture, attestation.
                        </div>
                    </div>
                    <Link
                        to="/prise-en-charge"
                        className="group inline-flex items-center gap-2 rounded-full border border-brand-ink/20 px-5 py-2.5 text-xs font-semibold text-brand-ink transition-all hover:border-brand-ink hover:bg-brand-ink hover:text-white"
                        data-testid="cursus-prise-en-charge-link"
                    >
                        Comprendre la prise en charge
                        <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                    </Link>
                </motion.div>
            </div>
        </section>
    );
}
